import { AxiosResponse } from 'axios';
import apiClient from '../axios';
import {
  Action,
  ActivitiesSort,
  ActivityLog,
  ActivityLogDetails,
  ICreateActivityLogParams,
  ICreatePlannedActivityParams,
  IGetActionsResponse,
  IResolveParams,
  PlannedActivitiesSort,
  PlannedActivity,
  PlannedActivityDetails,
  WorkLog,
} from './interfaces';
import {
  IGetResourceResponse,
  IPaginatedResourceEndpointResponse,
  IUpsertResourceResponse,
} from '../interfaces';
import { FilterFields } from '../../components/ManagedDataTable/interfaces';
import { getPaginatedEndpointParams } from '../helpers';

export const getActivityLogs = async (
  page: number,
  pageSize: number,
  sort?: ActivitiesSort,
  search?: string,
  filters?: FilterFields
): Promise<IPaginatedResourceEndpointResponse<ActivityLog>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<ActivityLog>
  > = await apiClient.get('/activities/logs', {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
      filters,
    }),
  });

  return response.data;
};

export const getActivityLog = async (
  id: number
): Promise<ActivityLogDetails> => {
  const response: AxiosResponse<IGetResourceResponse<ActivityLogDetails>> =
    await apiClient.get(`/activities/logs/${id}`);

  return response.data.data;
};

export const createActivityLog = async (
  activityLogData: ICreateActivityLogParams
): Promise<number> => {
  const payload = {
    ...activityLogData,
    performedAt: activityLogData.performedAt.format('YYYY-MM-DD HH:mm:ss'),
  };
  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.post(
    '/activities/logs/',
    payload
  );

  return response.data.data.id;
};

export const getActions = async (): Promise<Action[]> => {
  const response: AxiosResponse<IGetActionsResponse> = await apiClient.get(
    '/activities/actions'
  );

  return response.data.data;
};

export const updateActivityLog = async (
  activityLogId: number,
  activityLogData: ICreateActivityLogParams
): Promise<number> => {
  const payload = {
    ...activityLogData,
    performedAt: activityLogData.performedAt.format('YYYY-MM-DD HH:mm:ss'),
  };
  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.put(
    `/activities/logs/${activityLogId}`,
    payload
  );

  return response.data.data.id;
};

export const deleteActivityLog = async (id: number): Promise<void> => {
  await apiClient.delete(`/activities/logs/${id}`);
};

export const getPlannedActivities = async (
  page: number,
  pageSize: number,
  sort?: PlannedActivitiesSort,
  search?: string,
  filters?: FilterFields
): Promise<IPaginatedResourceEndpointResponse<PlannedActivity>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<PlannedActivity>
  > = await apiClient.get('/activities/planned', {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
      filters,
    }),
  });

  return response.data;
};

export const getPlannedActivity = async (
  id: number
): Promise<PlannedActivityDetails> => {
  const response: AxiosResponse<IGetResourceResponse<PlannedActivityDetails>> =
    await apiClient.get(`/activities/planned/${id}`);

  return response.data.data;
};

export const createPlannedActivity = async (
  plannedActivityData: ICreatePlannedActivityParams
): Promise<number> => {
  const payload = {
    ...plannedActivityData,
    performAt: plannedActivityData.performAt.format('YYYY-MM-DD'),
    startTime: plannedActivityData.startTime?.format('HH:mm'),
    endTime: plannedActivityData.endTime?.format('HH:mm'),
  };
  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.post(
    '/activities/planned/',
    payload
  );

  return response.data.data.id;
};

export const updatePlannedActivity = async (
  plannedActivityId: number,
  plannedActivityData: ICreatePlannedActivityParams
): Promise<number> => {
  const payload = {
    ...plannedActivityData,
    performAt: plannedActivityData.performAt.format('YYYY-MM-DD'),
    startTime: plannedActivityData.startTime?.format('HH:mm'),
    endTime: plannedActivityData.endTime?.format('HH:mm'),
  };
  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.put(
    `/activities/planned/${plannedActivityId}`,
    payload
  );

  return response.data.data.id;
};

export const deletePlannedActivity = async (id: number): Promise<void> => {
  await apiClient.delete(`/activities/planned/${id}`);
};

export const resolvePlannedActivity = async (
  plannedActivityId: number,
  resolveData: IResolveParams
): Promise<number> => {
  const payload = {
    ...resolveData,
    performAt: resolveData.performAt?.format('YYYY-MM-DD'),
    startTime: resolveData.startTime?.format('HH:mm') || null,
    endTime: resolveData.endTime?.format('HH:mm') || null,
  };
  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.put(
    `/activities/planned/${plannedActivityId}/resolve`,
    payload
  );

  return response.data.data.id;
};

export const getPlannedActivityWorkLog = async (
  id: number,
  page: number,
  pageSize: number,
  sort?: ActivitiesSort,
  search?: string,
  filters?: FilterFields
): Promise<IPaginatedResourceEndpointResponse<WorkLog>> => {
  const response: AxiosResponse<IPaginatedResourceEndpointResponse<WorkLog>> =
    await apiClient.get(`/activities/planned/${id}/work-log`, {
      params: getPaginatedEndpointParams({
        page,
        perPage: pageSize,
        sort,
        search,
        filters,
      }),
    });

  return response.data;
};
